import type { ReactNode } from 'react';
import { EM_DASH, MARK_GLYPHS } from './icons.js';
import './ChoiceTile.css';

export interface ChoiceTilePoint {
  text: string;
  /** `yes` = included (green check), `no` = excluded (muted dash). */
  mark: 'yes' | 'no';
}

export interface ChoiceTileProps {
  name: string;
  selected: boolean;
  onSelect?: () => void;
  /** Colour of the leading 10px dot. */
  tone?: 'neutral' | 'success' | 'info';
  /** Badge slot beside the name — "Current" in the mode picker. */
  badge?: ReactNode;
  /** Right-aligned mono metadata — a record ceiling, a region code. */
  meta?: string;
  blurb?: ReactNode;
  points?: ChoiceTilePoint[];
  /** A tile whose option genuinely cannot be chosen yet. Still rendered in full —
   * hiding an option misrepresents the choice — but not selectable. */
  disabled?: boolean;
}

/**
 * The radio-tile (design-system catalogue, Tier 2) — the prototype's pattern for
 * a choice among rich options: the whole tile is the control, with a 1.5px
 * accent border marking selection (audit D-5). Used by the mode picker; the
 * mode modal cannot be built from the 12px dot `Radio`.
 *
 * `role="radio"` + `aria-checked` on a button, grouped by the caller under
 * `role="radiogroup"` — the semantics of "one choice among alternatives" without
 * hiding a native input inside a 200px-tall label, which breaks in exactly the
 * ways invisible-input hacks always break.
 */
export function ChoiceTile({
  name,
  selected,
  onSelect,
  tone = 'neutral',
  badge,
  meta,
  blurb,
  points,
  disabled = false,
}: ChoiceTileProps) {
  return (
    <button
      type="button"
      className="dpr-choice-tile"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className="dpr-choice-tile__head">
        <span
          className={[
            'dpr-choice-tile__dot',
            tone !== 'neutral' ? `dpr-choice-tile__dot--${tone}` : null,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        />
        <span className="dpr-choice-tile__name">{name}</span>
        {badge}
        <span className="dpr-choice-tile__spacer" />
        {meta ? <span className="dpr-choice-tile__meta">{meta}</span> : null}
      </span>
      {blurb ? <p className="dpr-choice-tile__blurb">{blurb}</p> : null}
      {points && points.length > 0 ? (
        <span className="dpr-choice-tile__points">
          {points.map((point) => (
            <span key={point.text} className="dpr-choice-tile__point">
              {/* The mark is decorative; the text carries the meaning, and for a
                  "no" point the sentence itself says what is missing. */}
              <span
                className={`dpr-choice-tile__mark dpr-choice-tile__mark--${point.mark}`}
                aria-hidden="true"
              >
                {point.mark === 'yes' ? MARK_GLYPHS.yes : EM_DASH}
              </span>
              <span>{point.text}</span>
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}
